import { createShortMarketSource, type RoundFetch, type ShortMarketSource } from './api';
import type { Round } from './model';
import { indexRounds, isShortRoundMarket, normaliseEventId, type RoundRef } from './round-index';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

/** Query key of rounds.json. The 5-minute page also writes each of its poll results here. */
export const SHORT_ROUNDS_FEED_KEY = ['ShortRoundsFeed'];
/** The event gate waits at most this long for rounds.json, then opens the event page. */
export const FEED_TIMEOUT_MS = 4_000;

const configuredSource = (): ShortMarketSource | undefined => {
  const base = import.meta.env.VITE_SHORT_MARKETS_API_PATH;
  try {
    return base ? createShortMarketSource(base) : undefined;
  } catch (error) {
    // Not shown to users: a mistyped tenant path turns the 5-minute pages and the home card off.
    console.warn('short-markets: VITE_SHORT_MARKETS_API_PATH is not a same-origin path; feature off', base, error);
    return undefined;
  }
};
/** The tenant's static short-market feed; undefined when VITE_SHORT_MARKETS_API_PATH is unset or invalid. */
export const shortMarketSource = configuredSource();

export type ShortRoundsFeed = {
  /** Last good rounds.json; kept when a later fetch fails. */
  feed?: RoundFetch;
  /** Local time (ms) at which `feed` arrived. */
  updatedAt: number;
  /** The first attempt is still in flight: nothing is known yet. A failed attempt settles it (no retry is waited for). */
  checking: boolean;
  /** `feed` is cached and being read again (stale on mount): its answer is not in yet. */
  refreshing: boolean;
  /** The last read succeeded and none is in flight. It says nothing about how old `feed` is. */
  fresh: boolean;
};
type FeedOptions = { refetchInterval?: UseQueryOptions<RoundFetch>['refetchInterval'] };

/**
 * One feed read that settles by FEED_TIMEOUT_MS whatever the read does: a request that ignores its abort
 * signal must not hold the event gate's skeleton. `signal` aborts the read too.
 */
export const readWithinTimeout = <T>(signal: AbortSignal, read: (signal: AbortSignal) => Promise<T>): Promise<T> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      abort();
      reject(new Error(`Short-market feed timed out after ${FEED_TIMEOUT_MS}ms`));
    }, FEED_TIMEOUT_MS);
  });
  return Promise.race([read(controller.signal), timeout]).finally(() => {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  });
};
const fetchRounds = (source: ShortMarketSource, signal: AbortSignal) => readWithinTimeout(signal, s => source.rounds(s));

/**
 * Query options of rounds.json (key, fetcher, 30s stale, no retry): one definition for the hook and
 * for the boot prefetch (confs/boot-prefetch.ts), so both land on the same cache entry.
 */
export const shortRoundsFeedQueryOptions = (source: ShortMarketSource) => ({
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- one feed per tenant build: the key stays fixed (the 5-minute page writes to it too)
  queryKey: SHORT_ROUNDS_FEED_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => fetchRounds(source, signal),
  staleTime: 30_000,
  // A failed feed means "no 5-minute rounds"; nothing retries it in a loop.
  retry: false as const,
});

const useFeedQuery = ({ refetchInterval }: FeedOptions = {}): ShortRoundsFeed => {
  const query = useQuery({ ...shortRoundsFeedQueryOptions(shortMarketSource!), refetchInterval });
  return {
    feed: query.data,
    updatedAt: query.dataUpdatedAt,
    // After a failure react-query reports the retry on mount as pending again (no data yet); a feed that
    // already failed is "no rounds" at once, so the event gate never waits for that retry.
    checking: query.status === 'pending' && query.errorUpdateCount === 0,
    refreshing: query.status === 'success' && query.fetchStatus === 'fetching',
    fresh: query.status === 'success' && query.fetchStatus === 'idle',
  };
};
const NO_FEED: ShortRoundsFeed = { updatedAt: 0, checking: false, refreshing: false, fresh: false };
const useNoFeed = (): ShortRoundsFeed => NO_FEED;

/**
 * rounds.json through react-query (30s stale, no retry, 4s timeout), shared by the event gate,
 * the home card and the list filters. Without a configured feed there is no query and no request.
 */
export const useShortRoundsFeed: (options?: FeedOptions) => ShortRoundsFeed = shortMarketSource ? useFeedQuery : useNoFeed;

// Keyed on the rounds array: react-query keeps its identity while the rounds do not change.
const useRoundIndex = (rounds?: Round[]) => useMemo(() => indexRounds(rounds && { rounds }), [rounds]);

/**
 * What rounds.json says about an event id:
 * - listed: it is a round of tonight (decided at once, whatever a read in flight will say);
 * - checking: it is not listed yet, and the answer is still on its way (the first read, or a read of a stale cache);
 * - unlisted: the last read of rounds.json succeeded, none is in flight, and it does not list the id. It may be a
 *   round of an earlier night;
 * - unavailable: no feed, or the read failed or timed out. Nothing is known, so nothing else may rely on it.
 */
export type ShortRoundMatch = { kind: 'listed'; round: RoundRef } | { kind: 'checking' | 'unlisted' | 'unavailable' };
const CHECKING: ShortRoundMatch = { kind: 'checking' };
const UNLISTED: ShortRoundMatch = { kind: 'unlisted' };
const UNAVAILABLE: ShortRoundMatch = { kind: 'unavailable' };

export const useShortRoundMatch = (eventId: string): ShortRoundMatch => {
  const { feed, checking, refreshing, fresh } = useShortRoundsFeed();
  const index = useRoundIndex(feed?.rounds);
  const round = index.get(normaliseEventId(eventId));
  if (round) return { kind: 'listed', round };
  if (checking || refreshing) return CHECKING;
  return fresh ? UNLISTED : UNAVAILABLE;
};

/** Stable list predicate: true for a market of a 5-minute round (see isShortRoundMarket). */
export const useIsShortRoundMarket = () => {
  const index = useRoundIndex(useShortRoundsFeed().feed?.rounds);
  return useCallback((market: Parameters<typeof isShortRoundMarket>[0]) => isShortRoundMarket(market, index), [index]);
};
